/**
 * Легковесный шаблонизатор на основе лексера и рекурсивного спуска.
 * Корректно обрабатывает произвольную глубину вложенности {{#if}} и {{#each}}.
 */

type TokenType = 'TEXT' | 'VAR' | 'RAW_VAR' | 'IF_START' | 'ELSE' | 'IF_END' | 'EACH_START' | 'EACH_END';

interface Token {
  type: TokenType;
  content?: string;
}

// 1. ЛЕКСЕР: Разбивает строку на токены
function tokenize(template: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < template.length) {
    if (template.startsWith('{{', i)) {
      if (template.startsWith('{{{', i)) {
        const end = template.indexOf('}}}', i + 3);
        tokens.push({ type: 'RAW_VAR', content: template.slice(i + 3, end).trim() });
        i = end + 3;
      } else {
        const end = template.indexOf('}}', i + 2);
        const inner = template.slice(i + 2, end).trim();
        
        if (inner.startsWith('#if ')) tokens.push({ type: 'IF_START', content: inner.slice(4).trim() });
        else if (inner === 'else') tokens.push({ type: 'ELSE' });
        else if (inner === '/if') tokens.push({ type: 'IF_END' });
        else if (inner.startsWith('#each ')) tokens.push({ type: 'EACH_START', content: inner.slice(6).trim() });
        else if (inner === '/each') tokens.push({ type: 'EACH_END' });
        else tokens.push({ type: 'VAR', content: inner });
        
        i = end + 2;
      }
    } else {
      let textEnd = template.indexOf('{{', i);
      if (textEnd === -1) textEnd = template.length;
      tokens.push({ type: 'TEXT', content: template.slice(i, textEnd) });
      i = textEnd;
    }
  }
  return tokens;
}

// 2. ВСПОМОГАТЕЛЬНАЯ ФУНКЦИЯ: Извлечение блока токенов с учетом вложенности
function extractBlock(tokens: Token[], endType: TokenType): { trueBlock: Token[], falseBlock: Token[] } {
  let depth = 1;
  let trueBlock: Token[] = [];
  let falseBlock: Token[] = [];
  let isElse = false;

  while (tokens.length > 0 && depth > 0) {
    const token = tokens.shift()!;
    
    if (token.type === endType) {
      depth--;
      if (depth === 0) continue; // Не добавляем сам закрывающий тег в блок
    } else if (token.type === (endType === 'IF_END' ? 'IF_START' : 'EACH_START')) {
      depth++; // Увеличиваем глубину при вложенных блоках
    } else if (endType === 'IF_END' && token.type === 'ELSE' && depth === 1) {
      isElse = true;
      continue;
    }

    if (depth > 0) {
      if (isElse) falseBlock.push(token);
      else trueBlock.push(token);
    }
  }
  return { trueBlock, falseBlock };
}

// 3. ПАРСЕР: Рекурсивный обход токенов
function parse(tokens: Token[], context: Record<string, any>): string {
  let result = '';

  while (tokens.length > 0) {
    const token = tokens.shift()!;

    if (token.type === 'TEXT') {
      result += token.content!;
    } 
    else if (token.type === 'VAR') {
      const val = getNestedValue(context, token.content!);
      result += (val === undefined || val === null) ? '' : escapeHtml(String(val));
    } 
    else if (token.type === 'RAW_VAR') {
      const val = getNestedValue(context, token.content!);
      result += (val === undefined || val === null) ? '' : String(val);
    } 
    else if (token.type === 'IF_START') {
      const condition = getNestedValue(context, token.content!);
      const isTruthy = condition !== undefined && condition !== null && condition !== false && condition !== '' && (!Array.isArray(condition) || condition.length > 0);
      const { trueBlock, falseBlock } = extractBlock(tokens, 'IF_END');
      result += parse(isTruthy ? trueBlock : falseBlock, context);
    } 
    else if (token.type === 'EACH_START') {
      const array = getNestedValue(context, token.content!);
      const { trueBlock } = extractBlock(tokens, 'EACH_END');
      
      if (Array.isArray(array) && array.length > 0) {
        result += array.map((item: any, index: number) => {
          // Создаем новый контекст, добавляя 'item' и 'index', но сохраняя доступ к корню (например, i18n)
          return parse([...trueBlock], { ...context, item, index });
        }).join('');
      }
    }
  }
  return result;
}

// 4. ЭКСПОРТИРУЕМАЯ ФУНКЦИЯ
export function renderTemplate(template: string, data: Record<string, any>): string {
  const tokens = tokenize(template);
  return parse(tokens, data);
}

// Утилиты
function getNestedValue(obj: Record<string, any>, path: string): any {
  return path.split('.').reduce((current, key) => 
    (current && typeof current === 'object' && key in current ? current[key] : undefined), obj);
}

function escapeHtml(str: string): string {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}